import React from 'react';
import { Tooltip } from '@plerionhq/design-system';
import { CustomBreadcrumbs, type BreadcrumbItem } from './CustomBreadcrumbs';
import {
  Maximize,
  Minimize,
  Settings,
  User,
  Wrench,
  ChartNoAxesCombined,
  Rocket,
  ClipboardList,
  List,
  PanelRightClose,
  X,
  SlidersHorizontal,
  MessageSquare,
  ExternalLink,
  Sparkles,
  ScrollText,
} from 'lucide-react';
import { useLocation } from '@tanstack/react-router';
import { useChatStore } from '../../store/chatStore';
import { useModal } from '../../../../contexts/ModalContext';
import {
  OnboardingProgressSteps,
  type OnboardingStep,
} from './OnboardingProgressSteps';
import {
  usePinConversation,
  useListConversations,
} from '../../../../api/conversations';
import { ConversationMenu } from './ConversationMenu';
import { skillRegistry } from '../../../../components/onboarding/registry/SkillRegistry';
import { useGetTask } from '../../../../api/tasks';
import { useListAgentSkills } from '../../../../api/agent-skills';
import { decodeSkillRef } from '../../../../routes/settings/skills/utils/skillRef';
import { DEFAULT_TASK_TYPE } from '../../../../pages/tasks/utils';

import { useTranslation } from 'react-i18next';

// Route to label mapping
const routeLabels: Record<
  string,
  { name: string; icon?: React.ComponentType<{ className?: string }> }
> = {
  '/c': { name: 'Recent chats', icon: MessageSquare },
  '/quick-start': { name: 'Quickstart', icon: Rocket },
  '/tasks': { name: 'Tasks', icon: ClipboardList },
  '/dashboard': { name: 'Dashboards', icon: ChartNoAxesCombined },
  '/spotlights': { name: 'Spotlights', icon: Sparkles },
  '/settings': { name: 'Settings', icon: Settings },
  '/settings/memory': { name: 'Memory', icon: User },
  '/settings/tools': { name: 'Tools', icon: Wrench },
  '/settings/activities': { name: 'Activities', icon: List },
  '/settings/preferences': { name: 'Preferences', icon: SlidersHorizontal },
  '/settings/notification': { name: 'Notification', icon: MessageSquare },
  '/settings/about-you/questions': { name: 'Questions', icon: User },
  '/onboarding': { name: 'Onboarding' },
  '/skills': { name: 'Skills', icon: ScrollText },
};

interface ChatHeaderProps {
  isFullScreen?: boolean;
  isExtension?: boolean;
  onFullScreenToggle?: () => void;
  onClose?: () => void; // Collapse handler for mini mode
  className?: string;
  onboardingSteps?: OnboardingStep[];
  onOnboardingStepAction?: (step: OnboardingStep) => void;
  onOnboardingStepDismiss?: (step: OnboardingStep) => void;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  isFullScreen = false,
  isExtension = false,
  onFullScreenToggle,
  onClose,
  className = '',
  onboardingSteps = [],
  onOnboardingStepAction,
  onOnboardingStepDismiss,
}) => {
  const { t } = useTranslation('pleri/chat');
  const location = useLocation();

  // Normalize pathname (remove /pleri prefix if present)
  const pathname = location.pathname.replace('/pleri', '');
  const isConversationRoute = pathname.startsWith('/c/');
  const isConversationsListRoute = pathname === '/c' || pathname === '/c/';

  // Get active conversation data
  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );
  const conversationStoreTitle = useChatStore((state) =>
    activeConversationId
      ? (state.conversations[activeConversationId]?.title ?? null)
      : null,
  );
  const isNewChat = useChatStore((state) =>
    activeConversationId
      ? state.conversations[activeConversationId]?.messages?.length === 0
      : false,
  );
  const { openDeleteModal, openRenameModal } = useModal();
  const { mutate: pinConversation } = usePinConversation();
  const { data: pinnedData } = useListConversations({ pinned: true });
  const { data: allConversationsData } = useListConversations({});
  const PIN_LIMIT = 10;
  const isPinLimitReached =
    (pinnedData?.conversations || []).length >= PIN_LIMIT;

  // Get conversation title and pinned status from API data
  const conversationListItem = allConversationsData?.conversations?.find(
    (c) => c.conversationId === activeConversationId,
  );
  const conversationTitle =
    conversationListItem?.title || conversationStoreTitle || null;
  const isPinned = conversationListItem?.pinned || false;

  const handleDelete = () => {
    if (activeConversationId) {
      openDeleteModal(activeConversationId);
    }
  };

  const handlePin = () => {
    if (activeConversationId) {
      pinConversation({
        conversationId: activeConversationId,
        pinned: !isPinned,
      });
    }
  };

  const handleRename = () => {
    if (activeConversationId && conversationTitle) {
      openRenameModal(activeConversationId, conversationTitle);
    }
  };

  // Get task name if on a task detail page
  const taskMatch = pathname.match(/^\/tasks\/([^/]+)/);
  const taskId = taskMatch?.[1];
  const { data: task } = useGetTask(DEFAULT_TASK_TYPE, taskId || '');

  // Get skill name if on a skill detail page
  const skillRouteMatch = pathname.match(/^\/skills\/([^/]+)/);
  const { data: skills } = useListAgentSkills();

  // Build breadcrumb items for non-conversation routes
  const buildBreadcrumbItems = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [];

    // Check for tasks routes
    if (pathname.startsWith('/tasks')) {
      items.push({
        name: 'Tasks',
        icon: ({ className }) => <ClipboardList className={className} />,
        to: '/tasks',
      });

      // Check if it's a specific task
      if (taskMatch && taskMatch[1]) {
        const taskName = task?.name || taskMatch[1];
        items.push({
          name: taskName,
        });
      }
    }
    // Check for dashboard routes
    else if (pathname.startsWith('/dashboard')) {
      items.push({
        name: 'Dashboards',
        icon: ({ className }) => <ChartNoAxesCombined className={className} />,
        to: '/dashboard',
      });

      // Check if it's a specific dashboard
      const dashboardMatch = pathname.match(/^\/dashboard\/([^/]+)/);
      if (dashboardMatch && dashboardMatch[1]) {
        const dashboardId = dashboardMatch[1];
        // Try to get dashboard title from hook
        try {
          // Note: This is a synchronous call, so we can't use hooks here
          // For now, show the dashboardId. Could be enhanced with a dashboard store
          items.push({
            name: dashboardId,
          });
        } catch {
          items.push({
            name: dashboardId,
          });
        }
      }
    }
    // Check for settings route
    else if (pathname.startsWith('/settings')) {
      items.push({
        name: 'Settings',
        icon: ({ className }) => <Settings className={className} />,
        to: '/settings',
      });

      // Check for settings sub-routes
      const settingsSubRoute = pathname.replace('/settings', '');
      if (settingsSubRoute && settingsSubRoute !== '/') {
        // Handle tools/skill/$skillId route
        const skillMatch = settingsSubRoute.match(/^\/tools\/skill\/([^/]+)/);
        if (skillMatch && skillMatch[1]) {
          items.push({
            name: 'Tools',
            icon: ({ className }) => <Wrench className={className} />,
            to: '/settings/tools',
          });
          // Try to get skill name from registry
          const skillRegistration = skillRegistry.getSkill(skillMatch[1]);
          const skillName = skillRegistration?.config?.name || skillMatch[1];
          items.push({
            name: skillName,
          });
        }
        // Handle tools/workspace/$workspaceId route
        else {
          const workspaceMatch = settingsSubRoute.match(
            /^\/tools\/workspace\/([^/]+)/,
          );
          if (workspaceMatch && workspaceMatch[1]) {
            items.push({
              name: 'Tools',
              icon: ({ className }) => <Wrench className={className} />,
              to: '/settings/tools',
            });
            items.push({
              name: workspaceMatch[1],
            });
          } else {
            // Handle other settings sub-routes
            const routeInfo =
              routeLabels[settingsSubRoute] ||
              routeLabels[`/settings${settingsSubRoute}`];
            if (routeInfo && routeInfo.icon) {
              const Icon = routeInfo.icon;
              items.push({
                name: routeInfo.name,
                icon: ({ className }) => <Icon className={className} />,
              });
            } else if (routeInfo) {
              items.push({
                name: routeInfo.name,
              });
            }
          }
        }
      }
    } else if (pathname.startsWith('/skills/')) {
      items.push({
        name: 'Skills',
        icon: ({ className }) => <ScrollText className={className} />,
        to: '/skills',
      });
      if (pathname === '/skills/library') {
        items.push({ name: 'Skill Library' });
      } else if (pathname === '/skills/new') {
        items.push({ name: 'New skill' });
      } else if (skillRouteMatch?.[1]) {
        const { scope, name } = decodeSkillRef(skillRouteMatch[1]);
        const matched = (skills ?? []).find(
          (s) => s.scope === scope && s.name === name,
        );
        items.push({ name: matched?.displayName ?? name });
      }
    } else {
      // For non-settings routes, add the current route label
      const routeInfo = routeLabels[pathname];
      if (routeInfo && routeInfo.icon) {
        const Icon = routeInfo.icon;
        items.push({
          name: routeInfo.name,
          icon: ({ className }) => <Icon className={className} />,
        });
      } else if (routeInfo) {
        items.push({
          name: routeInfo.name,
        });
      }
    }

    return items;
  };

  const hasOnboardingSteps =
    Array.isArray(onboardingSteps) && onboardingSteps.length > 0;

  // For conversation routes, show conversation title
  if (isConversationRoute) {
    return (
      <div
        className={`relative z-50 flex items-center bg-white shadow-xl !shadow-white dark:bg-gray-900 dark:!shadow-gray-900 ${className}`}
      >
        {/* Left: Conversation title and actions */}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {conversationTitle && (
            <>
              <h1 className="truncate text-sm font-normal text-gray-600 dark:text-gray-400">
                {conversationTitle}
              </h1>
              <ConversationMenu
                onDelete={handleDelete}
                onPin={handlePin}
                onRename={handleRename}
                align="left"
                isPinned={isPinned}
                isPinDisabled={!isPinned && isPinLimitReached}
                pinLimitMessage={`Only ${PIN_LIMIT} chats can be pinned at a time`}
              />
            </>
          )}
        </div>
        {/* Middle: Onboarding progress steps - absolutely centered - only show for new chats */}
        {hasOnboardingSteps && isNewChat && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <OnboardingProgressSteps
              steps={onboardingSteps}
              onStepAction={onOnboardingStepAction}
              onDismiss={onOnboardingStepDismiss}
            />
          </div>
        )}
        {/* Right: Fullscreen toggle and collapse button */}
        <div className="ml-auto flex items-center gap-2">
          {onFullScreenToggle && (
            <Tooltip
              label={
                isExtension && !isFullScreen
                  ? t('openANewTab')
                  : isFullScreen
                    ? t('exitFullScreen')
                    : t('expandToFullScreen')
              }
              placement="bottom"
            >
              <button
                onClick={onFullScreenToggle}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[4.5px] bg-gray-100 text-gray-900 transition-all duration-150 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-600"
                data-testid="pleri-chat-header-full-screen-toggle"
                aria-label={
                  isExtension && !isFullScreen
                    ? t('openANewTab')
                    : isFullScreen
                      ? t('exitFullScreen')
                      : t('expandToFullScreen')
                }
              >
                {isFullScreen ? (
                  <Minimize className="h-5 w-5" />
                ) : isExtension ? (
                  <ExternalLink className="h-5 w-5" />
                ) : (
                  <Maximize className="h-5 w-5" />
                )}
              </button>
            </Tooltip>
          )}
          {onClose && (
            <Tooltip label={t('dismissPleri')} placement="bottom">
              <button
                onClick={onClose}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[4.5px] bg-gray-100 text-gray-900 transition-all duration-150 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-600"
                data-testid="pleri-chat-header-collapse"
                aria-label={t('dismissPleri')}
              >
                {isFullScreen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <PanelRightClose className="h-5 w-5" />
                )}
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  // For conversations list route, show header with buttons but no title
  if (isConversationsListRoute) {
    return (
      <div
        className={`relative z-50 flex items-center bg-white shadow-xl !shadow-white dark:bg-gray-900 dark:!shadow-gray-900 ${className}`}
      >
        {/* Left: Empty - no title */}
        <div className="flex min-w-0 flex-1 items-center" />
        {/* Middle: Onboarding progress steps - absolutely centered - only show for new chats */}
        {hasOnboardingSteps && isNewChat && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <OnboardingProgressSteps
              steps={onboardingSteps}
              onStepAction={onOnboardingStepAction}
              onDismiss={onOnboardingStepDismiss}
            />
          </div>
        )}
        {/* Right: Fullscreen toggle and collapse button */}
        <div className="ml-auto flex items-center gap-2">
          {onFullScreenToggle && (
            <Tooltip
              label={
                isExtension && !isFullScreen
                  ? t('openANewTab')
                  : isFullScreen
                    ? t('exitFullScreen')
                    : t('expandToFullScreen')
              }
              placement="bottom"
            >
              <button
                onClick={onFullScreenToggle}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[4.5px] bg-gray-100 text-gray-900 transition-all duration-150 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-600"
                data-testid="pleri-chat-header-full-screen-toggle"
                aria-label={
                  isExtension && !isFullScreen
                    ? t('openANewTab')
                    : isFullScreen
                      ? t('exitFullScreen')
                      : t('expandToFullScreen')
                }
              >
                {isFullScreen ? (
                  <Minimize className="h-5 w-5" />
                ) : isExtension ? (
                  <ExternalLink className="h-5 w-5" />
                ) : (
                  <Maximize className="h-5 w-5" />
                )}
              </button>
            </Tooltip>
          )}
          {onClose && (
            <Tooltip label={t('dismissPleri')} placement="bottom">
              <button
                onClick={onClose}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[4.5px] bg-gray-100 text-gray-900 transition-all duration-150 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-600"
                data-testid="pleri-chat-header-collapse"
                aria-label={t('dismissPleri')}
              >
                {isFullScreen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <PanelRightClose className="h-5 w-5" />
                )}
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  // For non-conversation routes, show breadcrumb
  const breadcrumbItems = buildBreadcrumbItems();

  // Don't render if no items
  if (breadcrumbItems.length === 0) {
    return null;
  }

  return (
    <div
      className={`relative z-10 flex items-center border-b border-gray-200 bg-white shadow-xl !shadow-white dark:border-gray-700 dark:bg-gray-900 dark:!shadow-gray-900 ${className}`}
    >
      {/* Left: Breadcrumb */}
      <div className="flex min-w-0 flex-1 items-center">
        <CustomBreadcrumbs items={breadcrumbItems} />
      </div>
      {/* Right: Fullscreen toggle and collapse button */}
      <div className="flex flex-shrink-0 items-center gap-2">
        {onFullScreenToggle && (
          <Tooltip
            label={
              isExtension && !isFullScreen
                ? t('openANewTab')
                : isFullScreen
                  ? t('exitFullScreen')
                  : t('expandToFullScreen')
            }
            placement="bottom"
          >
            <button
              onClick={onFullScreenToggle}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[4.5px] bg-gray-100 text-gray-900 transition-all duration-150 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-600"
              data-testid="pleri-chat-header-full-screen-toggle"
              aria-label={
                isExtension && !isFullScreen
                  ? t('openANewTab')
                  : isFullScreen
                    ? t('exitFullScreen')
                    : t('expandToFullScreen')
              }
            >
              {isFullScreen ? (
                <Minimize className="h-5 w-5" />
              ) : isExtension ? (
                <ExternalLink className="h-5 w-5" />
              ) : (
                <Maximize className="h-5 w-5" />
              )}
            </button>
          </Tooltip>
        )}
        {onClose && (
          <Tooltip label={t('collapse')} placement="bottom">
            <button
              onClick={onClose}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[4.5px] bg-gray-100 text-gray-900 transition-all duration-150 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-600"
              data-testid="pleri-chat-header-collapse"
              aria-label={t('collapse')}
            >
              <PanelRightClose className="h-5 w-5" />
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
